import { useState, useEffect } from 'react';
import { 
  History, 
  RefreshCw, 
  AlertCircle, 
  Search
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserWorkflowHistory() {
  const { session } = useAuth();
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/workflow-history', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load workflow history');
      const data = await res.json();
      if (data.success) {
        setHistory(data.data || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [session]);

  const filtered = history.filter(h => 
    h.use_case_number?.toLowerCase().includes(search.toLowerCase()) ||
    h.use_case_title?.toLowerCase().includes(search.toLowerCase()) ||
    h.action?.toLowerCase().includes(search.toLowerCase()) ||
    h.performed_by?.toLowerCase().includes(search.toLowerCase()) ||
    h.to_status?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <History className="h-5 w-5 text-blue-500" />
            <span>Workflow Governance History</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Immutable chronological audit log of all state transitions, approvals, and stakeholder actions.
          </p>
        </div>

        <button
          onClick={fetchHistory}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Filter history by action, user, case number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* TIMELINE / TABLE */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">Loading immutable workflow records from Backend API...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground">
            <History className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-foreground">No workflow history records found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                <tr>
                  <th className="py-3 px-4 font-semibold">Timestamp</th>
                  <th className="py-3 px-4 font-semibold">Use Case</th>
                  <th className="py-3 px-4 font-semibold">Action</th>
                  <th className="py-3 px-4 font-semibold">Previous Status</th>
                  <th className="py-3 px-4 font-semibold">New Status</th>
                  <th className="py-3 px-4 font-semibold">Performed By</th>
                  <th className="py-3 px-4 font-semibold">Comments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((item, idx) => (
                  <tr key={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                      {new Date(item.timestamp).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-blue-500">
                      {item.use_case_number}
                    </td>
                    <td className="py-3 px-4 font-semibold text-foreground">
                      {item.action}
                    </td>
                    <td className="py-3 px-4">
                      {item.from_status ? (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-muted text-muted-foreground">
                          {item.from_status}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic text-[11px]">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/20">
                        {item.to_status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-foreground">{item.performed_by}</span>
                        <span className="text-[10px] text-muted-foreground">({item.role || 'User'})</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground max-w-xs truncate">
                      {item.comments || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
